import React from 'react';
import { Pill } from './WorkflowFormControls';

/**
 * Renders a component / ontology definition by what its data *is*, not by how deeply
 * its transport representation happens to nest.
 *
 * The connector publishes no schema for these blobs, so the shape has to be read at
 * runtime — but reading it as "object → definition list, array → list of definition
 * lists" turns a three-check trigger into a metre of key-above-value stacking. Every
 * branch below exists because a real card looked wrong rendered by the previous one:
 *
 *   • a list of same-shaped rows (`checks`, `steps`, `intents`) is a table;
 *   • scalars are facts and belong side by side in a strip, not one per row with a
 *     150px gutter of white space next to a lone `0.8`;
 *   • genuinely irregular data falls back to numbered sub-cards at every depth.
 */

// ─── Shape probes ────────────────────────────────────────────────────────────

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isScalar(v: unknown): v is string | number | boolean | null {
  return v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** Short enough to sit inline next to its label rather than claim its own block. */
function isShortScalar(v: unknown): boolean {
  if (typeof v === 'string') return v.length <= 72 && v.indexOf('\n') === -1;
  return typeof v === 'number' || typeof v === 'boolean';
}

/** Short enough to survive inside a table cell. */
function isChipList(v: unknown): v is Array<string | number> {
  return Array.isArray(v) && v.length > 0 && v.every((x) => (typeof x === 'string' && x.length <= 44) || typeof x === 'number');
}

/**
 * Labels or enum members — as opposed to sentences. Both arrive as `string[]`, and
 * setting chips against a rule ("Offer a solution before the problem is confirmed")
 * is what made a strategy's `do` and `dont` render two different ways.
 */
function isLabelList(v: unknown): v is Array<string | number> {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((x) => typeof x === 'number' || (typeof x === 'string' && x.length <= 30 && x.trim().split(/\s+/).length <= 4))
  );
}

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (isPlainObject(v)) return Object.keys(v).length === 0;
  return v === '';
}

/** Backend keys are easier to scan with separators turned into spaces. */
function humanize(key: string): string {
  return key.replace(/[_-]+/g, ' ').trim();
}

const MAX_TABLE_COLUMNS = 6;
const MAX_ROWS = 40;

// ─── Table detection ─────────────────────────────────────────────────────────

/** Keys that already number the rows, so the table's own `#` column would repeat them. */
const ORDINAL_KEYS = ['order', 'index', 'position', 'rank', 'step', 'no', 'num'];

export function hasOwnOrdinal(columns: string[]): boolean {
  return columns.some((c) => ORDINAL_KEYS.indexOf(c.toLowerCase()) !== -1);
}

/**
 * Columns for an array of objects, or `null` when the array is not really tabular.
 * A mostly-empty table of unrelated keys reads worse than a stack of cards, so the
 * decision is made on how full the grid would actually be — not on key names.
 */
export function tableColumns(rows: unknown[]): string[] | null {
  if (rows.length < 2 || !rows.every(isPlainObject)) return null;
  const objects = rows as Record<string, unknown>[];

  const cols: string[] = [];
  objects.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (cols.indexOf(k) === -1) cols.push(k);
    });
  });
  if (cols.length === 0 || cols.length > MAX_TABLE_COLUMNS) return null;

  // One nested object anywhere and every cell in that column becomes an unreadable stub.
  const cellFits = (v: unknown) =>
    v === undefined || v === null || (isScalar(v) && String(v).length <= 90) || (isChipList(v) && v.length <= 6);
  if (!objects.every((row) => cols.every((c) => cellFits(row[c])))) return null;

  // At least one key must run through every row, or these are unrelated things in a list.
  if (!cols.some((c) => objects.every((r) => r[c] !== undefined))) return null;

  const filled = objects.reduce((n, row) => n + cols.filter((c) => row[c] !== undefined).length, 0);
  if (filled / (objects.length * cols.length) < 0.6) return null;

  return cols;
}

// ─── Leaf values ─────────────────────────────────────────────────────────────

/** A value small enough to render inline — in a table cell, a fact, or a chip row. */
export const DefInline: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') return <span className="cwf-def__muted">—</span>;
  if (typeof value === 'boolean') return <Pill tone={value ? 'ok' : 'neutral'}>{value ? 'yes' : 'no'}</Pill>;
  if (typeof value === 'number') return <span className="cwf-def__num">{value}</span>;
  if (isChipList(value)) {
    return (
      <span className="cwf-chips cwf-chips--tight">
        {value.map((x, i) => (
          <span key={i} className="cwf-chip cwf-chip--muted">
            {String(x)}
          </span>
        ))}
      </span>
    );
  }
  return <span className="cwf-def__str">{String(value)}</span>;
};

const DefText: React.FC<{ value: string }> = ({ value }) => <p className="cwf-def__text">{value}</p>;

const DefTable: React.FC<{ rows: Record<string, unknown>[]; columns: string[] }> = ({ rows, columns }) => {
  const shown = rows.slice(0, MAX_ROWS);
  const numbered = !hasOwnOrdinal(columns);
  return (
    <div className="cwf-deftable__scroll">
      <table className="cwf-deftable">
        <thead>
          <tr>
            {numbered ? (
              <th className="cwf-deftable__idx" scope="col">
                #
              </th>
            ) : null}
            {columns.map((c) => (
              <th key={c} scope="col" title={c}>
                {humanize(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, i) => (
            <tr key={i}>
              {numbered ? <td className="cwf-deftable__idx">{i + 1}</td> : null}
              {columns.map((c) => (
                <td key={c}>
                  <DefInline value={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length ? <p className="cwf-def__more">+{rows.length - shown.length} more rows</p> : null}
    </div>
  );
};

// ─── Composite values ────────────────────────────────────────────────────────

const DefComposite: React.FC<{ value: unknown; depth: number }> = ({ value, depth }) => {
  if (typeof value === 'string') return <DefText value={value} />;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="cwf-def__muted">empty list</span>;
    if (isLabelList(value)) return <DefInline value={value} />;

    // Sentence-length strings (a strategy's do / don't rules) — a bulleted list, not chips.
    if (value.every((x) => typeof x === 'string' || typeof x === 'number')) {
      const shown = value.slice(0, MAX_ROWS);
      return (
        <ul className="cwf-def__list">
          {shown.map((x, i) => (
            <li key={i}>{String(x)}</li>
          ))}
          {value.length > shown.length ? <li className="cwf-def__muted">+{value.length - shown.length} more</li> : null}
        </ul>
      );
    }

    const columns = tableColumns(value);
    if (columns) return <DefTable rows={value as Record<string, unknown>[]} columns={columns} />;

    const shown = value.slice(0, MAX_ROWS);
    return (
      <div className="cwf-def__items">
        {shown.map((item, i) => (
          <div key={i} className="cwf-def__item">
            <span className="cwf-def__item-index">{i + 1}</span>
            <div className="cwf-def__item-body">
              {isPlainObject(item) ? <DefBlocks obj={item} depth={depth + 1} /> : <DefValue value={item} depth={depth + 1} />}
            </div>
          </div>
        ))}
        {value.length > shown.length ? <p className="cwf-def__more">+{value.length - shown.length} more items</p> : null}
      </div>
    );
  }

  if (isPlainObject(value)) {
    if (Object.keys(value).length === 0) return <span className="cwf-def__muted">empty object</span>;
    return (
      <div className="cwf-def__nested">
        <DefBlocks obj={value} depth={depth + 1} />
      </div>
    );
  }

  return <DefInline value={value} />;
};

/** Any value, inline when it fits and as a block when it does not. */
export const DefValue: React.FC<{ value: unknown; depth?: number }> = ({ value, depth = 0 }) => {
  if (isScalar(value) && isShortScalar(value)) return <DefInline value={value} />;
  return <DefComposite value={value} depth={depth} />;
};

// ─── An object, split into facts and blocks ──────────────────────────────────

/**
 * The layout that replaced the old two-column definition list: short scalars gather
 * into one strip of facts at the top, and everything with real content gets a titled
 * block at full width underneath.
 */
export const DefBlocks: React.FC<{ obj: Record<string, unknown>; depth?: number; skip?: string[] }> = ({
  obj,
  depth = 0,
  skip = [],
}) => {
  const entries = Object.entries(obj).filter(([k, v]) => skip.indexOf(k) === -1 && v !== undefined);
  if (entries.length === 0) return <span className="cwf-def__muted">no fields</span>;

  const facts = entries.filter(([, v]) => isShortScalar(v) || isEmptyValue(v));
  const blocks = entries.filter(([, v]) => !(isShortScalar(v) || isEmptyValue(v)));

  return (
    <div className="cwf-defblocks">
      {facts.length ? (
        <dl className="cwf-facts">
          {facts.map(([k, v]) => (
            <div key={k} className="cwf-fact">
              <dt className="cwf-fact__key" title={k}>
                {humanize(k)}
              </dt>
              <dd className="cwf-fact__val">
                <DefInline value={isEmptyValue(v) ? null : v} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {blocks.map(([k, v]) => (
        <section key={k} className="cwf-defblock">
          <header className="cwf-defblock__head">
            <h4 className="cwf-defblock__title" title={k}>
              {humanize(k)}
            </h4>
            <span className="cwf-defblock__count">{blockCount(v)}</span>
          </header>
          <DefValue value={v} depth={depth} />
        </section>
      ))}
    </div>
  );
};

function blockCount(v: unknown): string {
  if (Array.isArray(v)) return `${v.length} ${v.length === 1 ? 'item' : 'items'}`;
  if (isPlainObject(v)) {
    const n = Object.keys(v).length;
    return `${n} ${n === 1 ? 'field' : 'fields'}`;
  }
  if (typeof v === 'string') {
    const n = v.trim().split(/\s+/).length;
    return `${n} ${n === 1 ? 'word' : 'words'}`;
  }
  return '';
}

// ─── Collapsed-card preview ──────────────────────────────────────────────────

export interface DefSignal {
  key: string;
  label: string;
  value: string;
}

/**
 * The few numbers worth showing on a card that is still closed — enough to tell two
 * triggers apart without opening either.
 */
export function definitionSignals(definition: unknown, skip: string[] = [], limit = 3): DefSignal[] {
  if (!isPlainObject(definition)) return [];
  const out: DefSignal[] = [];
  Object.entries(definition).forEach(([k, v]) => {
    if (skip.indexOf(k) !== -1 || out.length >= limit || isEmptyValue(v)) return;
    if (typeof v === 'number' || typeof v === 'boolean') out.push({ key: k, label: humanize(k), value: String(v) });
    else if (typeof v === 'string' && v.length <= 24 && v.indexOf('\n') === -1) out.push({ key: k, label: humanize(k), value: v });
    else if (Array.isArray(v)) out.push({ key: k, label: humanize(k), value: String(v.length) });
  });
  return out;
}
